"use client";

import { type FormEvent, useEffect, useState } from "react";

import { useJoinWaitlist, useWaitlistCount } from "@nomiss/hooks";
import { ResponseError } from "@nomiss/sdk";

import { ArrowIcon } from "@/app/(public)/waitlist/_components/arrow-icon";
import { WAITLIST_EMAIL_INPUT_ID } from "@/app/(public)/waitlist/_components/waitlist-constants";
import { WaitlistSuccessModal } from "@/app/(public)/waitlist/_components/waitlist-success-modal";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

async function getApiErrorMessage(error: unknown): Promise<string | null> {
  if (!(error instanceof ResponseError)) {
    return null;
  }

  try {
    const body = (await error.response.clone().json()) as { message?: unknown };
    return typeof body.message === "string" ? body.message : null;
  } catch {
    return null;
  }
}

export function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");

  const { joinWaitlistMutation } = useJoinWaitlist();
  const { waitlistCountQuery } = useWaitlistCount();
  const count = waitlistCountQuery.data?.data?.count ?? 0;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");

    const trimmed = email.trim();
    if (!trimmed) {
      setError("Please enter your email.");
      return;
    }
    if (!emailPattern.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }

    setStatus("loading");

    try {
      const result = await joinWaitlistMutation.mutateAsync({ email: trimmed });
      if (!result.data?.isNew) {
        setError("You're already on our list!");
        setStatus("idle");
        return;
      }
      setStatus("success");
      setEmail("");
    } catch (caught) {
      const apiMessage = await getApiErrorMessage(caught);
      setError(apiMessage ?? "Something went wrong. Please try again.");
      setStatus("idle");
    }
  }

  useEffect(() => {
    const input = document.getElementById(WAITLIST_EMAIL_INPUT_ID);
    if (!input) return;

    const handleFocus = () => {
      setTimeout(() => {
        input.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
    };

    input.addEventListener("focus", handleFocus);
    return () => input.removeEventListener("focus", handleFocus);
  }, []);

  return (
    <>
      <form onSubmit={handleSubmit} className="mt-5 w-full lg:max-w-xl" noValidate>
        {count <= 1000 ? (
          <p className="sm:text-md md:text-md lg:text-md mb-1 text-sm font-bold text-gray-200">
            <span className="text-brand-lime">First 1,000 members:</span> every sport, free for a
            year.
          </p>
        ) : (
          <p className="sm:text-md md:text-md lg:text-md text-brand-lime mb-1 text-sm font-bold">
            Be the first to know when we go live.
          </p>
        )}

        <div className="flex flex-row items-stretch gap-3">
          <input
            id={WAITLIST_EMAIL_INPUT_ID}
            name="email"
            type="email"
            autoComplete="email"
            aria-label="Email address"
            placeholder="Drop your email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (error) setError("");
            }}
            disabled={status === "loading"}
            className="waitlist-email-input"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className="waitlist-submit-button inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap transition-opacity">
            {status === "loading" ? "Joining…" : "I'm in"}
            <ArrowIcon className="text-[#0f0f0f]" />
          </button>
        </div>
        {error ? (
          <p className="mt-2 px-1 text-sm text-red-400" role="alert">
            {error}
          </p>
        ) : null}

        <p className="sm:text-md mt-2 mb-2 text-xs text-gray-400">
          One email when we go live. No spam, no scores spoiled.
        </p>
      </form>
      {status === "success" ? <WaitlistSuccessModal onClose={() => setStatus("idle")} /> : null}
    </>
  );
}
